import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModuleService } from '../../../services/module.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { MaterialModule } from '../../../shared/material.module';
import { Tenant } from '../../../models/tenant.model';
import { Module } from '../../../models/module.model';

export interface TenantModuleAssignmentData {
  tenant: Tenant;
}

export interface TenantModule {
  id: string;
  tenantId: string;
  moduleId: string;
  isActive: boolean;
  module: Module;
}

@Component({
  selector: 'app-tenant-module-assignment',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './tenant-module-assignment.component.html',
  styleUrl: './tenant-module-assignment.component.scss'
})
export class TenantModuleAssignmentComponent implements OnInit {
  tenant: Tenant;
  allModules: Module[] = [];
  assignedModules: TenantModule[] = [];
  isLoading = false;
  searchTerm = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TenantModuleAssignmentData,
    private dialogRef: MatDialogRef<TenantModuleAssignmentComponent>,
    private moduleService: ModuleService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService
  ) {
    this.tenant = data.tenant;
  }

  ngOnInit(): void {
    this.loadModules();
    this.loadAssignedModules();
  }

  loadModules(): void {
    this.moduleService.getAllModules().subscribe({
      next: (modules: Module[]) => {
        this.allModules = modules;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
      }
    });
  }

  loadAssignedModules(): void {
    this.moduleService.getTenantModules(this.tenant.id).subscribe({
      next: (tenantModules: TenantModule[]) => {
        this.assignedModules = tenantModules;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
      }
    });
  }

  get filteredModules(): Module[] {
    if (!this.searchTerm.trim()) {
      return this.allModules;
    }

    const term = this.searchTerm.toLowerCase();
    return this.allModules.filter(module =>
      module.name.toLowerCase().includes(term) ||
      module.description?.toLowerCase().includes(term) ||
      module.type?.toLowerCase().includes(term)
    );
  }

  isModuleAssigned(moduleId: string): boolean {
    return this.assignedModules.some(tm => tm.moduleId === moduleId);
  }

  getAssignedModule(moduleId: string): TenantModule | undefined {
    return this.assignedModules.find(tm => tm.moduleId === moduleId);
  }

  toggleModuleAssignment(module: Module): void {
    const isAssigned = this.isModuleAssigned(module.id);
    
    if (isAssigned) {
      this.unassignModule(module);
    } else {
      this.assignModule(module);
    }
  }

  assignModule(module: Module): void {
    this.isLoading = true;
    this.moduleService.assignModuleToTenant(this.tenant.id, {
      moduleId: module.id,
      isActive: true
    }).subscribe({
      next: () => {
        this.toastService.success('Module Assigned', `${module.name} has been assigned to ${this.tenant.name}`);
        this.loadAssignedModules();
        this.isLoading = false;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        this.isLoading = false;
      }
    });
  }

  unassignModule(module: Module): void {
    this.isLoading = true;
    this.moduleService.unassignModuleFromTenant(this.tenant.id, module.id).subscribe({
      next: () => {
        this.toastService.success('Module Unassigned', `${module.name} has been unassigned from ${this.tenant.name}`);
        this.loadAssignedModules();
        this.isLoading = false;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        this.isLoading = false;
      }
    });
  }

  toggleModuleStatus(module: Module): void {
    const assignedModule = this.getAssignedModule(module.id);
    if (!assignedModule) return;

    this.isLoading = true;
    this.moduleService.updateTenantModuleAssignment(this.tenant.id, module.id, {
      isActive: !assignedModule.isActive
    }).subscribe({
      next: () => {
        this.toastService.success('Module Status Updated', `${module.name} status has been updated`);
        this.loadAssignedModules();
        this.isLoading = false;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        this.isLoading = false;
      }
    });
  }

  // Bulk Actions
  assignAll(): void {
    this.isLoading = true;
    let completed = 0;
    const total = this.allModules.filter(m => !this.isModuleAssigned(m.id)).length;
    
    if (total === 0) {
      this.toastService.info('All Assigned', 'All modules are already assigned to this tenant');
      this.isLoading = false;
      return;
    }

    this.allModules.forEach(module => {
      if (!this.isModuleAssigned(module.id)) {
        this.moduleService.assignModuleToTenant(this.tenant.id, {
          moduleId: module.id,
          isActive: true
        }).subscribe({
          next: () => {
            completed++;
            if (completed === total) {
              this.toastService.success('Bulk Assignment Complete', `${total} modules assigned to ${this.tenant.name}`);
              this.loadAssignedModules();
              this.isLoading = false;
            }
          },
          error: (error: any) => {
            completed++;
            const errorResult = this.errorHandler.handleError(error);
            console.error(`Failed to assign ${module.name}:`, errorResult.message);
            if (completed === total) {
              this.loadAssignedModules();
              this.isLoading = false;
            }
          }
        });
      }
    });
  }

  unassignAll(): void {
    // Filter out core modules from unassignment
    const nonCoreModules = this.assignedModules.filter(tm => !tm.module.isCore);
    const total = nonCoreModules.length;
    
    if (total === 0) {
      if (this.assignedModules.length > 0) {
        this.toastService.info('Core Modules Protected', 'Only core modules are assigned. Core modules cannot be unassigned.');
      } else {
        this.toastService.info('No Assignments', 'No modules are currently assigned to this tenant');
      }
      return;
    }

    this.isLoading = true;
    let completed = 0;

    nonCoreModules.forEach(tenantModule => {
      this.moduleService.unassignModuleFromTenant(this.tenant.id, tenantModule.moduleId).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.toastService.success('Bulk Unassignment Complete', `${total} modules unassigned from ${this.tenant.name}`);
            this.loadAssignedModules();
            this.isLoading = false;
          }
        },
        error: (error: any) => {
          completed++;
          const errorResult = this.errorHandler.handleError(error);
          console.error(`Failed to unassign ${tenantModule.module.name}:`, errorResult.message);
          if (completed === total) {
            this.loadAssignedModules();
            this.isLoading = false;
          }
        }
      });
    });
  }

  activateAll(): void {
    this.isLoading = true;
    let completed = 0;
    const toActivate = this.assignedModules.filter(tm => !tm.isActive);
    const total = toActivate.length;
    
    if (total === 0) {
      this.toastService.info('All Active', 'All assigned modules are already active');
      this.isLoading = false;
      return;
    }

    toActivate.forEach(tenantModule => {
      this.moduleService.updateTenantModuleAssignment(this.tenant.id, tenantModule.moduleId, {
        isActive: true
      }).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.toastService.success('Bulk Activation Complete', `${total} modules activated for ${this.tenant.name}`);
            this.loadAssignedModules();
            this.isLoading = false;
          }
        },
        error: (error: any) => {
          completed++;
          const errorResult = this.errorHandler.handleError(error);
          console.error(`Failed to activate ${tenantModule.module.name}:`, errorResult.message);
          if (completed === total) {
            this.loadAssignedModules();
            this.isLoading = false;
          }
        }
      });
    });
  }

  deactivateAll(): void {
    this.isLoading = true;
    let completed = 0;
    const toDeactivate = this.assignedModules.filter(tm => tm.isActive);
    const total = toDeactivate.length;
    
    if (total === 0) {
      this.toastService.info('All Inactive', 'All assigned modules are already inactive');
      this.isLoading = false;
      return;
    }

    toDeactivate.forEach(tenantModule => {
      this.moduleService.updateTenantModuleAssignment(this.tenant.id, tenantModule.moduleId, {
        isActive: false
      }).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.toastService.success('Bulk Deactivation Complete', `${total} modules deactivated for ${this.tenant.name}`);
            this.loadAssignedModules();
            this.isLoading = false;
          }
        },
        error: (error: any) => {
          completed++;
          const errorResult = this.errorHandler.handleError(error);
          console.error(`Failed to deactivate ${tenantModule.module.name}:`, errorResult.message);
          if (completed === total) {
            this.loadAssignedModules();
            this.isLoading = false;
          }
        }
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
